import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface Establishment {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  googleMapsUrl: string | null;
  googleBusinessUrl: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  isActive: boolean;
  planType: string | null;
  trialEndsAt: string | null;
  bookingNotice: string | null;
  onboarding_completed: boolean;
  createdAt: string | null;
}

interface EstablishmentContextType {
  currentEstablishment: Establishment | null;
  establishmentId: string | null;
  slug: string | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const EstablishmentContext = createContext<EstablishmentContextType | undefined>(undefined);

interface EstablishmentProviderProps {
  children: ReactNode;
  slugFromUrl?: string; // For public pages where slug comes from URL
  defaultSlug?: string | null; // For subdomain-based routing
}

export function EstablishmentProvider({ children, slugFromUrl, defaultSlug }: EstablishmentProviderProps) {
  // Use defaultSlug if slugFromUrl is not provided (subdomain routing)
  const effectiveSlug = slugFromUrl || defaultSlug || undefined;
  const { establishmentId: authEstablishmentId, isSuperAdmin, loading: authLoading } = useAuth();
  const [currentEstablishment, setCurrentEstablishment] = useState<Establishment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEstablishment = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase.from('establishments' as any).select('*');

      if (effectiveSlug) {
        // Public page: fetch by slug (from URL or subdomain)
        query = query.eq('slug', effectiveSlug).eq('is_active', true);
      } else if (authEstablishmentId) {
        // Authenticated user: fetch by establishment ID
        query = query.eq('id', authEstablishmentId);
      } else if (isSuperAdmin) {
        // Super admin without specific establishment: just load without errors
        setLoading(false);
        return;
      } else {
        // No establishment context available
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await query.single();

      if (fetchError) {
        console.error('[EstablishmentContext] Error fetching establishment:', fetchError);
        setError(new Error(fetchError.message));
        setCurrentEstablishment(null);
      } else if (data) {
        const estData = data as any;
        setCurrentEstablishment({
          id: estData.id,
          name: estData.name,
          slug: estData.slug,
          email: estData.email,
          phone: estData.phone,
          address: estData.address,
          city: estData.city,
          postalCode: estData.postal_code,
          country: estData.country,
          googleMapsUrl: estData.google_maps_url,
          googleBusinessUrl: estData.google_business_url,
          websiteUrl: estData.website_url,
          logoUrl: estData.logo_url,
          isActive: estData.is_active,
          planType: estData.plan_type,
          trialEndsAt: estData.trial_ends_at,
          bookingNotice: estData.booking_notice,
          onboarding_completed: estData.onboarding_completed ?? false,
          createdAt: estData.created_at ?? null,
        });
      }
    } catch (e) {
      console.error('[EstablishmentContext] Exception:', e);
      setError(e instanceof Error ? e : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchEstablishment();
    }
  }, [authEstablishmentId, effectiveSlug, authLoading, isSuperAdmin]);

  return (
    <EstablishmentContext.Provider
      value={{
        currentEstablishment,
        establishmentId: currentEstablishment?.id || authEstablishmentId,
        slug: currentEstablishment?.slug || null,
        loading: loading || authLoading,
        error,
        refetch: fetchEstablishment,
      }}
    >
      {children}
    </EstablishmentContext.Provider>
  );
}

export const useEstablishment = () => {
  const context = useContext(EstablishmentContext);
  if (!context) {
    throw new Error('useEstablishment must be used within EstablishmentProvider');
  }
  return context;
};

// Hook for fetching establishment by slug (for public pages)
export function useEstablishmentBySlug(slug: string | undefined) {
  const [establishment, setEstablishment] = useState<Establishment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchBySlug = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from('establishments' as any)
          .select('*')
          .eq('slug', slug)
          .eq('is_active', true)
          .single();

        if (fetchError) {
          setError(new Error(fetchError.message));
          setEstablishment(null);
        } else if (data) {
          const estData = data as any;
          setEstablishment({
            id: estData.id,
            name: estData.name,
            slug: estData.slug,
            email: estData.email,
            phone: estData.phone,
            address: estData.address,
            city: estData.city,
            postalCode: estData.postal_code,
            country: estData.country,
            googleMapsUrl: estData.google_maps_url,
            googleBusinessUrl: estData.google_business_url,
            websiteUrl: estData.website_url,
            logoUrl: estData.logo_url,
            isActive: estData.is_active,
            planType: estData.plan_type,
            trialEndsAt: estData.trial_ends_at,
            bookingNotice: estData.booking_notice,
            onboarding_completed: estData.onboarding_completed ?? false,
            createdAt: estData.created_at ?? null,
          });
        }
      } catch (e) {
        setError(e instanceof Error ? e : new Error('Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchBySlug();
  }, [slug]);

  return { establishment, loading, error };
}
